import styled, { css } from 'styled-components';
import { rem } from 'polished';

const shimmer = css`
  @keyframes productsLandingSkelShimmer {
    0% {
      background-position: 300% 0;
    }
    100% {
      background-position: -300% 0;
    }
  }
`;

export const LandingSkel = styled.div`
  ${shimmer}
  height: ${props => props.$height || rem('12px')};
  width: ${props => props.$width || '100%'};
  max-width: ${props => props.$maxWidth || '100%'};
  border-radius: ${props => props.$radius || rem('4px')};
  flex-shrink: ${props => (props.$shrink === false ? 1 : 0)};
  background: linear-gradient(90deg, rgba(0, 0, 0, 0.04) 0%, rgba(0, 0, 0, 0.1) 40%, rgba(0, 0, 0, 0.04) 80%);

  body[data-theme='dark'] & {
    background: linear-gradient(90deg, rgba(255, 255, 255, 0.04) 0%, rgba(255, 255, 255, 0.14) 40%, rgba(255, 255, 255, 0.04) 80%);
  }
  background-size: 300% 100%;
  animation: productsLandingSkelShimmer 1.8s ease-in-out infinite;
  animation-delay: ${props => props.$delay || '0s'};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0.75;
    
    body[data-theme='dark'] & {
      opacity: 0.55;
    }
  }
`;

export const HeaderSectionSkeletonRoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem('16px')};
  width: 100%;
  min-width: 0;

  @media screen and (max-width: 1023px) {
    flex-wrap: wrap;
    row-gap: ${rem('12px')};
  }
`;

export const HeaderSectionSkeletonMain = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${rem('6px')};
`;

export const HeaderSectionSkeletonActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: nowrap;
  gap: ${rem('12px')};
  flex-shrink: 0;

  @media screen and (max-width: 1023px) {
    flex: 1 1 100%;
    width: 100%;
    flex-wrap: wrap;
    justify-content: flex-end;
  }
`;

export const SearchSkel = styled(LandingSkel)`
  flex: 0 0 ${rem('280px')};
  width: ${rem('280px')};
  max-width: ${rem('280px')};
  height: ${rem('40px')};
  border-radius: ${rem('7px')};

  @media screen and (max-width: 640px) {
    flex: 1 1 ${rem('220px')};
    width: auto;
    max-width: 100%;
  }
`;

export const ActionBtnSkel = styled(LandingSkel)`
  height: ${rem('36px')};
  width: ${rem('132px')};
  border-radius: ${rem('6px')};
`;

export const CatalogSkeletonShell = styled.div`
  width: 100%;
  box-sizing: border-box;
  flex: 1;
  min-height: ${rem('260px')};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #ffffff;
  
  body[data-theme='dark'] & {
    background: #022443;
  }
  border: none;
  
  body[data-theme='dark'] & {
    border: 1px solid #2F76C5;
  }
  border-radius: ${rem('4px')};
  
  body[data-theme='dark'] & {
    border-radius: ${rem('12px')};
  }
  box-shadow: 1px 1px 5px rgba(0, 0, 0, 0.2);

  body[data-theme='dark'] & {
    box-shadow: -2px 2px 4px #2f76c5;
  }
`;

export const CatalogSkeletonHeadRow = styled.div`
  display: grid;
  grid-template-columns: ${props => props.$columns || 'repeat(7, minmax(0, 1fr))'};
  align-items: center;
  gap: ${rem('8px')};
  min-height: ${rem('44px')};
  padding: 0 ${rem('12px')};
  background: #eeeeee;
  
  body[data-theme='dark'] & {
    background: #263E59;
  }
  border-bottom: 1px solid #e0e0e0;

  body[data-theme='dark'] & {
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }
`;

export const CatalogSkeletonBody = styled.div`
  flex: 1;
  min-height: 0;
`;

export const CatalogSkeletonRow = styled.div`
  display: grid;
  grid-template-columns: ${props => props.$columns || 'repeat(7, minmax(0, 1fr))'};
  align-items: center;
  gap: ${rem('8px')};
  padding: ${rem('10px')} ${rem('12px')};
  border-bottom: 1px solid #e8eaed;

  body[data-theme='dark'] & {
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  }
`;

export const CatalogSkeletonCell = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: ${props => props.$align || 'flex-start'};
`;

export const CatalogSkeletonFooter = styled.div`
  display: grid;
  grid-template-columns: minmax(160px, 1fr) auto minmax(160px, 1fr);
  align-items: center;
  gap: ${rem('16px')};
  padding: ${rem('8px')} ${rem('16px')} ${rem('12px')};
  border-top: 1px solid #e8eaed;

  body[data-theme='dark'] & {
    border-top-color: rgba(255, 255, 255, 0.08);
  }
  background: #fff;
  
  body[data-theme='dark'] & {
    background: #022443;
  }
`;

export const BottomActionsSkeleton = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${rem('8px')};
  width: 100%;
  margin-top: ${rem('12px')};
  padding-top: ${rem('4px')};
`;

const PAGE_GUTTER = rem('16px');
const APP_SHELL_HEIGHT = rem('50px');

/** Full-page shell for Suspense / initial /products load (matches ProductsPageRoot). */
export const ProductsPageSkeletonShell = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - ${APP_SHELL_HEIGHT});
  max-height: calc(100vh - ${APP_SHELL_HEIGHT});
  min-height: 0;
  overflow: hidden;
  background: #f2f3f5;
  
  body[data-theme='dark'] & {
    background: transparent;
  }
`;

export const ProductsCrumbSkeletonStrip = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem('10px')};
  height: ${rem('55px')};
  min-height: ${rem('55px')};
  padding: 0 ${PAGE_GUTTER};
  box-sizing: border-box;
  background: #ffffff;
  
  body[data-theme='dark'] & {
    background: #0B2033;
  }
  box-shadow: 1px 2px 6px rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
`;

export const ProductsPageSkeletonBody = styled.div`
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const ProductsPageSkeletonIntro = styled.div`
  flex-shrink: 0;
  padding: ${rem('10px')} ${PAGE_GUTTER} ${rem('12px')};
`;

export const ProductsPageSkeletonMain = styled.div`
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 0 ${PAGE_GUTTER} ${PAGE_GUTTER};
  box-sizing: border-box;
  overflow: hidden;
`;
